import { useNavigate } from 'react-router-dom';
import { useTenantQuery } from '@/hooks/useTenantQuery';
import { useAuthStore } from '@/store/auth.store';
import { useIncidentStore } from '@/store/incident.store';
import FaIcon from '@/components/common/FaIcon';

interface CommandTransfer {
  id: string;
  incident_id: string;
  incoming_commander_id: string;
  outgoing_commander_id: string;
  status: string;
}

/**
 * Lightweight top-bar badge shown when the active incident has a pending
 * Transfer of Command involving the current user (incoming or outgoing).
 * Clicking navigates to the incident detail page.
 */
export default function GlobalTransferIndicator() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const { activeIncident } = useIncidentStore();

  const { data } = useTenantQuery<CommandTransfer[]>(
    ['command-transfers', activeIncident?.id ?? 'none'],
    activeIncident ? `/incidents/${activeIncident.id}/transfers` : '/incidents/none/transfers',
    {
      enabled: !!activeIncident?.id,
      staleTime: 30_000,
      queryOptions: { refetchInterval: 60_000 },
    },
  );

  const transfers: CommandTransfer[] = Array.isArray(data) ? data : [];

  if (!activeIncident || !user) return null;

  // `pending_acceptance` is an open transfer — the incoming commander asked for
  // clarification and has not accepted yet. Omitting it made this indicator
  // disappear at exactly the point it is most needed: the banner is how either
  // commander gets back into the transfer, and without it a transfer awaiting
  // acceptance had no entry point from anywhere in the app. The backend treats
  // the same three states as open (transfer-of-command.service).
  const pending = transfers.find(
    (t: CommandTransfer) =>
      (t.incoming_commander_id === user.id || t.outgoing_commander_id === user.id) &&
      (t.status === 'initiated' ||
        t.status === 'briefing_complete' ||
        t.status === 'pending_acceptance'),
  );

  if (!pending) return null;

  const isIncoming = pending.incoming_commander_id === user.id;

  return (
    <button
      type="button"
      onClick={() => navigate(`/incidents/${activeIncident.id}`)}
      title={`Transfer of Command in progress — ${activeIncident.name}`}
      className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 border border-amber-300 dark:border-amber-700 hover:bg-amber-200 dark:hover:bg-amber-800/40 transition-colors text-xs font-medium"
    >
      <FaIcon icon="right-left" className="h-3.5 w-3.5 shrink-0" />
      <span className="hidden sm:inline truncate max-w-[180px]">
        {isIncoming ? 'ToC Pending' : 'ToC In Progress'} — {activeIncident.name}
      </span>
      <span className="sm:hidden">ToC</span>
    </button>
  );
}
